import React, {useEffect, useState} from 'react';

import {IMG_DIR} from '#Client/components/constants';
import {Modal} from '#Client/components/Modal/Modal';
import {
  checkStyle,
  colorsContainer,
  settingsItem,
  subtitle,
  swatch,
} from '#Client/components/SettingsModal/SettingsModal.css';
import {trackEvent} from '#Client/components/util';
import {useSavedData} from '#Client/context/SavedData';
import {useDebounce} from '#Client/hooks/useDebounce';
import {CheckSvg} from '#Client/images/Check';
import {updateUserName} from '#Common/api/api';
import {backgroundColors} from '#Common/savedData';

type SettingsModalProps = {
  onClose: () => void;
  difficulty: number;
  setDifficulty: (difficulty: number) => void;
};

export const SettingsModal = ({
  onClose,
  difficulty,
  setDifficulty,
}: SettingsModalProps) => {
  const {savedData, updateSavedUserName, updateBackgroundColor} =
    useSavedData();
  const [userName, setUserName] = useState<string>(savedData.userName || '');
  const [saveResponseMessage, setSaveResponseMessage] = useState<string>('');
  const difficultySelectOnChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    trackEvent('click', 'general', 'select', 'settings-difficulty');
    setDifficulty(parseInt(event.target.value, 10));
  };

  const debouncedUserName = useDebounce(userName, 1000);

  const backgroundColor = savedData.backgroundColor || 'solid_light_blue';

  useEffect(() => {
    (async () => {
      if (debouncedUserName && debouncedUserName !== savedData.userName) {
        let response;
        try {
          response = await updateUserName(debouncedUserName);
          setSaveResponseMessage(`Saved your name: ${response.name}`);
        } catch (e) {
          setSaveResponseMessage(
            'Unable to save your name. Please try again later.',
          );
        }
        updateSavedUserName(debouncedUserName);
      }
    })();
  }, [debouncedUserName]);

  return (
    <Modal
      image={`${IMG_DIR}icon_settings.png`}
      title="Settings"
      onClose={onClose}
    >
      <>
        <div className={settingsItem}>
          <h2>Difficulty</h2>
          <select value={difficulty} onChange={difficultySelectOnChange}>
            <option value={9}>Easy</option>
            <option value={7}>Medium</option>
            <option value={5}>Hard</option>
            <option value={3}>Brutal</option>
          </select>
        </div>
        <div className={settingsItem}>
          <h2>Background Color</h2>
          <fieldset>
            <legend>Letter patterns</legend>
            <div className={colorsContainer}>
              {backgroundColors
                .filter(color => color.startsWith('letters_'))
                .map(color => (
                  <div
                    className={`${swatch} bg_${color}`}
                    key={color}
                    onClick={() => {
                      trackEvent(
                        'click',
                        'settings',
                        'background-color',
                        color,
                      );
                      updateBackgroundColor(color);
                    }}
                  >
                    {backgroundColor === color ? (
                      <CheckSvg className={checkStyle} />
                    ) : null}
                  </div>
                ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Solid Colors</legend>
            <div className={colorsContainer}>
              {backgroundColors
                .filter(color => color.startsWith('solid_'))
                .map(color => (
                  <div
                    className={`${swatch} bg_${color}`}
                    key={color}
                    onClick={() => {
                      trackEvent(
                        'click',
                        'settings',
                        'background-color',
                        color,
                      );
                      updateBackgroundColor(color);
                    }}
                  >
                    {backgroundColor === color ? (
                      <CheckSvg className={checkStyle} />
                    ) : null}
                  </div>
                ))}
            </div>
          </fieldset>
        </div>
        <div className={settingsItem}>
          <h2>Your name </h2>
          <div className={subtitle}>(for the leaderboard)</div>
          <input
            value={userName}
            onChange={event => {
              setUserName(event.target.value);
            }}
          />
          {saveResponseMessage !== '' && <div>{saveResponseMessage}</div>}
        </div>
        <div className={settingsItem}>
          <h2>Feedback</h2>
          <a
            href="https://twitter.com/word_crunch"
            rel="noreferrer"
            target="_blank"
          >
            Twitter
          </a>
        </div>
      </>
    </Modal>
  );
};
